import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Plus, Layers, Loader2, X, BookOpen } from "lucide-react";
import { toast } from "@/lib/toastBanner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CohortTracks({ cohortId, cohortStatus }) {
  const [assigned, setAssigned] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPicker, setShowPicker] = useState(false);
  const [allTracks, setAllTracks] = useState([]);
  const [pickerLoading, setPickerLoading] = useState(false);

  const fetchAssigned = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/cohorts/${cohortId}/tracks`, { withCredentials: true });
      setAssigned(data);
    } catch {
      toast.error("Failed to load tracks");
    } finally {
      setLoading(false);
    }
  }, [cohortId]);

  useEffect(() => {
    fetchAssigned();
  }, [fetchAssigned]);

  const openPicker = async () => {
    setShowPicker(true);
    setPickerLoading(true);
    try {
      const { data } = await axios.get(`${API}/tracks`, { withCredentials: true });
      setAllTracks(data);
    } catch {
      toast.error("Failed to load tracks");
    } finally {
      setPickerLoading(false);
    }
  };

  const handleAssign = async (trackId) => {
    try {
      await axios.post(`${API}/cohorts/${cohortId}/tracks`, { track_id: trackId }, { withCredentials: true });
      toast.success("Track assigned");
      fetchAssigned();
      setShowPicker(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to assign");
    }
  };

  const handleUnassign = async (trackId, title) => {
    if (!window.confirm(`Remove "${title}" from this cohort?`)) return;
    try {
      await axios.delete(`${API}/cohorts/${cohortId}/tracks/${trackId}`, { withCredentials: true });
      toast.success("Track removed");
      fetchAssigned();
    } catch {
      toast.error("Failed to remove");
    }
  };

  const isArchived = cohortStatus !== "active";

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin" style={{ color: "var(--primary-color, #4A90D9)" }} />
      </div>
    );
  }

  const assignedIds = new Set(assigned.map((t) => t.id));

  return (
    <div className="space-y-4" data-testid="cohort-tracks-tab">
      {!isArchived && (
        <Button
          onClick={openPicker}
          data-testid="assign-track-btn"
          className="h-9 px-4 font-body text-sm font-medium text-white rounded-lg hover:opacity-90"
          style={{ backgroundColor: "var(--primary-color, #4A90D9)", color: "var(--on-secondary, #FFFFFF)" }}
        >
          <Plus className="h-4 w-4 mr-1.5" />
          Assign Track
        </Button>
      )}

      {assigned.length === 0 ? (
        <div className="bg-[#11141A] border border-dashed border-white/10 rounded-xl p-12 text-center" data-testid="cohort-tracks-empty">
          <Layers className="h-10 w-10 text-[#6E7787] mx-auto mb-3" />
          <p className="text-[#6E7787] font-body">No tracks assigned to this cohort yet.</p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="cohort-tracks-list">
          {assigned.map((t) => (
            <div
              key={t.id}
              className="bg-[#11141A] border border-white/10 rounded-lg px-4 py-3.5 hover:border-white/20 transition-all"
              data-testid={`cohort-track-${t.id}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <Layers className="h-4 w-4 shrink-0" style={{ color: "var(--primary-color, #4A90D9)" }} />
                    <h4 className="text-sm font-body font-medium text-white truncate">{t.title}</h4>
                    <Badge
                      className={
                        t.status === "published"
                          ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/20"
                          : "bg-amber-500/15 text-amber-400 border-amber-500/20"
                      }
                    >
                      {t.status}
                    </Badge>
                  </div>
                  {t.description && (
                    <p className="text-xs text-[#6E7787] font-body ml-6 line-clamp-1">{t.description}</p>
                  )}
                  <div className="flex items-center gap-1.5 text-xs text-[#6E7787] font-body ml-6 mt-1">
                    <BookOpen className="h-3 w-3" />
                    {t.item_count || t.lexp_count || 0} item{(t.item_count || t.lexp_count || 0) !== 1 ? "s" : ""}
                  </div>
                </div>
                {!isArchived && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleUnassign(t.id, t.title)}
                    data-testid={`unassign-track-${t.id}`}
                    className="text-red-400 hover:text-red-300 hover:bg-red-500/10 text-xs h-7 px-2 shrink-0"
                  >
                    <X className="h-3.5 w-3.5 mr-1" />
                    Remove
                  </Button>
                )}
              </div>
            </div>
          ))}
          <p className="text-xs text-[#6E7787] font-body pt-1">
            {assigned.length} track{assigned.length !== 1 ? "s" : ""} assigned
          </p>
        </div>
      )}

      {/* Track Picker */}
      <Dialog open={showPicker} onOpenChange={setShowPicker}>
        <DialogContent className="bg-[#11141A] border-white/10 text-white max-w-lg max-h-[80vh] overflow-y-auto" data-testid="track-picker-modal">
          <DialogHeader>
            <DialogTitle className="font-body font-bold text-xl text-white">Assign Track</DialogTitle>
            <DialogDescription className="text-[#6E7787] font-body">
              Select a track to assign to this cohort.
            </DialogDescription>
          </DialogHeader>
          {pickerLoading ? (
            <div className="flex justify-center py-10">
              <Loader2 className="h-6 w-6 animate-spin" style={{ color: "var(--primary-color, #4A90D9)" }} />
            </div>
          ) : allTracks.length === 0 ? (
            <p className="text-[#6E7787] font-body text-sm py-6 text-center">No tracks available. Create one in Track Library first.</p>
          ) : (
            <div className="space-y-2 mt-2">
              {allTracks.map((t) => {
                const alreadyAssigned = assignedIds.has(t.id);
                return (
                  <div
                    key={t.id}
                    className={`border border-white/10 rounded-lg p-3 transition-all ${
                      alreadyAssigned ? "opacity-50 cursor-not-allowed" : "cursor-pointer hover:border-white/20"
                    }`}
                    onClick={() => !alreadyAssigned && handleAssign(t.id)}
                    data-testid={`picker-track-${t.id}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <div className="flex items-center gap-2">
                          <h4 className="text-sm font-body font-medium text-white">{t.title}</h4>
                          {t.status !== "published" && (
                            <span className="px-1.5 py-0.5 text-[10px] font-body rounded bg-amber-500/15 text-amber-300 border border-amber-500/20 uppercase tracking-wide">
                              {t.status || "draft"}
                            </span>
                          )}
                        </div>
                        {t.description && (
                          <p className="text-xs text-[#6E7787] font-body mt-0.5 line-clamp-1">{t.description}</p>
                        )}
                        <p className="text-xs text-[#6E7787] font-body mt-1">
                          {t.item_count || t.lexp_count || 0} item{(t.item_count || t.lexp_count || 0) !== 1 ? "s" : ""}
                        </p>
                      </div>
                      {alreadyAssigned ? (
                        <Badge className="bg-white/5 text-[#6E7787] border-white/10 shrink-0">Assigned</Badge>
                      ) : (
                        <Plus className="h-4 w-4 text-[#6E7787] shrink-0 mt-0.5" />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
